import React from 'react';
 
import Icon from '../AppIcon';
 

const Button = React.forwardRef(({
    children,
    variant = 'primary',
    size = 'md',
    disabled = false,
    type = 'button',
    className = '',
    onClick,
    fullWidth = false,
    isLoading = false,
    loading, // Add support for loading prop
    iconName,
    iconSize = 16,
    iconPosition = 'left',
    ...rest
}, ref) => {
    // Handle both isLoading and loading props (for backward compatibility)
    const isLoadingState = isLoading || loading === true;
    
    // Remove loading and iconPosition from rest props to prevent them from being passed to the DOM
    const { loading: _, iconPosition: __, ...cleanRest } = rest;
    // Simple base styles
    const baseClasses = 'inline-flex items-center justify-center px-4 py-2 rounded font-medium transition-colors';
    
    // Simple variant styles
    const variantClasses = {
        primary: 'text-white hover:opacity-90 transition-opacity' /* gradient applied via style */,
        secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300',
        outline: 'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50',
        ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-900',
        danger: 'text-white hover:opacity-90 transition-opacity' /* gradient applied via style */,
        error: 'bg-red-600 text-white hover:bg-red-700 transition-colors',
        success: 'text-white hover:opacity-90 transition-opacity' /* gradient applied via style */,
        destructive: 'text-white hover:opacity-90 transition-opacity' /* gradient applied via style */,
        accent: 'text-white hover:opacity-90 transition-opacity' /* gradient applied via style */,
    };

    // Gradient style for primary, danger, success, destructive, and accent variants
    const getGradientStyle = () => {
        if (variant === 'primary' || variant === 'danger' || variant === 'success' || variant === 'destructive' || variant === 'accent') {
            return {
                background: 'linear-gradient(135deg, rgb(79, 70, 229) 0%, rgb(124, 58, 237) 100%)',
            };
        }
        return {};
    };

    // Size classes
    const sizeClasses = {
        sm: 'px-1 py-1 text-sm',
        md: 'px-4 py-2',
        lg: 'px-6 py-3 text-lg'
    };

    // Build className
    const widthClass = fullWidth ? 'w-full' : '';
    const sizeClass = sizeClasses[size] || sizeClasses.md;
    const variantClass = variantClasses[variant] || variantClasses.primary;
    const disabledClass = (disabled || isLoadingState) ? 'opacity-50 cursor-not-allowed' : '';
    
    const finalClassName = `${baseClasses} ${variantClass} ${sizeClass} ${widthClass} ${disabledClass} ${className}`.trim();

    return (
        <button
            ref={ref}
            type={type}
            className={finalClassName}
            disabled={disabled || isLoadingState}
            onClick={onClick}
            style={getGradientStyle()}
            {...cleanRest}
        >
            {isLoadingState ? (
                <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    {children}
                </>
            ) : (
                <>
                    {iconName && iconPosition === 'left' && (
                        <Icon name={iconName} size={iconSize} className="mr-2" />
                    )}
                    {children}
                    {iconName && iconPosition === 'right' && (
                        <Icon name={iconName} size={iconSize} className="ml-2" />
                    )}
                </>
            )}
        </button>
    );
});

Button.displayName = 'Button';

export default Button;